define('common/ko/extension/validations',[
    'module',
    'knockout',
    'underscore',
    'moment',
    'regexp',
    'bluebird'
], function (module, ko, _, moment, rE, Promise) {

    ko.subscribable.fn.logger = ko.subscribable.fn.logger || ko.observable();

    /**
     * Convenience function for creating asynchronous validation methods
     * @param validationFunction will be called with (value, param, command) and should return true if the value
     *        validates given the parameters in param, otherwise the error
     * @param error what to return if validation fails
     * @param acceptBlank will consider a blank value valid if set to true
     */
    var createValidationMethod = function (validationFunction, error, acceptBlank) {
        return function (value, param, command) {
            var deferred = $.Deferred();

            // Deferred just to not block while running the validation logic
            _.defer(function () {
                var isValid = acceptBlank && _.str.isBlank(value);

                // Support conditional validators
                if (_.isObject(param) && param.onlyIf !== undefined) {
                    isValid = !param.onlyIf;
                    param = param.param;
                }

                try {
                    deferred.resolve(isValid || (validationFunction(value, param, command) || error));
                } catch (e) {
                    deferred.reject(e);
                }
            });
            return deferred;
        };
    };

    //Matches emails
    //Stolen from http://fightingforalostcause.net/misc/2006/compare-email-regex.php
    var emailRegEx = /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i;

    //Numbers with decimals and optional minus-sign (f.ex 1, 1.0, -1, -1.0)
    var numberRegEx = rE('^\\-?\\d+(\\.[0-9]+)?$');

    //Integer
    var integerRegEx = rE('^\\d+$');

    // VISA/Mastercard bins regex
    var supportedCardRegex = rE('^(4|51|52|53|54|55)');

    // Maestro regex
    var maestroRegex = rE('^(5018|5020|5038|5893|6304|6759|6761|6762|6763|0604)');

    //Letters, digits, and underscores
    var alfanumericRegEx = rE('^(\\w+)$');

    //Coupon code
    var couponCodeRegEx = rE('^[a-zA-Z0-9]+$');

    //Phone prefix, e.g. +1234
    var phonePrefixRegEx = rE('^\\+\\d{1,4}$');

    //Highly granular validation patterns for composite validation
    var containSpecialCharacter = rE('[\\W]');
    var containNumber = rE('[\\d]');
    var containCharacter = rE('[a-zA-Z]');

    var customValidationMethod = function (validator, voca) {
        return createValidationMethod(function (value, param) {
            return validator(value);
        }, voca);
    };

    var validationMethods = {
        custom: function (value, param) {
            if (_.isUndefined(param.validator)) {
                throw 'You must specify a validator function to your custom validation';
            }

            if (_.isUndefined(param.voca)) {
                throw 'You must specify a voca to your custom validation';
            }
            return customValidationMethod(param.validator, param.voca)(value, param);
        },
        customAsync: function (value, param) {
            return param.validator().then(function (result) {
                return !!result || param.voca;
            });
        },
        required: createValidationMethod(function (value, param) {
            return !param || !_.str.isBlank(value);
        }, 'REQUIRED'),
        options: createValidationMethod(function (value, param) {
            return _.includes(param, value);
        }, 'NOT_AN_OPTION'),
        minLength: createValidationMethod(function (value, param) {
            return (value.length >= param);
        }, 'TOO_SHORT', true),
        maxLength: createValidationMethod(function (value, param) {
            return (value.length <= param);
        }, 'TOO_LONG', true),
        //Less than
        lt: createValidationMethod(function (value, param) {
            if (numberRegEx.test(value)) {
                value = parseFloat(value);
            }
            if (numberRegEx.test(param)) {
                param = parseFloat(param);
            }
            return value < param;
        }, 'TOO_BIG', true),

        //Greater than
        gt: createValidationMethod(function (value, param) {
            if (numberRegEx.test(value)) {
                value = parseFloat(value);
            }
            if (numberRegEx.test(param)) {
                param = parseFloat(param);
            }
            return value > param;
        }, 'TOO_SMALL', true),
        //Less than or equal
        lteq: createValidationMethod(function (value, param) {
            if (numberRegEx.test(value)) {
                value = parseFloat(value);
            }
            if (numberRegEx.test(param)) {
                param = parseFloat(param);
            }
            return value <= param;
        }, 'TOO_BIG', true),
        //Greater than or equal
        gteq: createValidationMethod(function (value, param) {
            if (numberRegEx.test(value)) {
                value = parseFloat(value);
            }
            if (numberRegEx.test(param)) {
                param = parseFloat(param);
            }
            return value >= param;
        }, 'TOO_SMALL', true),

        ltdate: createValidationMethod(function (value, param) {
            return moment(value).isBefore(moment(param));
        }, 'TOO_BIG', true),

        lteqdate: createValidationMethod(function (value, param) {
            return moment(value).isSame(moment(param)) || moment(value).isBefore(moment(param));
        }, 'TOO_BIG', true),

        gtdate: createValidationMethod(function (value, param) {
            return moment(value).isAfter(moment(param));
        }, 'TOO_SMALL', true),

        gteqdate: createValidationMethod(function (value, param) {
            return moment(value).isSame(moment(param)) || moment(value).isAfter(moment(param));
        }, 'TOO_SMALL', true),
        moment: createValidationMethod(function (value, param) {
            return value._isAMomentObject && value.isValid();
        }, 'NOT_DATE', true),
        email: createValidationMethod(function (value, param) {
            return emailRegEx.test(value);
        }, 'NOT_EMAIL', true),
        number: createValidationMethod(function (value, param) {
            return numberRegEx.test(value);
        }, 'NOT_NUMBER', true),
        integer: createValidationMethod(function (value, params) {
            return integerRegEx.test(value);
        }, 'NOT_INTEGER', true),
        alfanumeric: createValidationMethod(function (value, params) {
            return _.isUndefined(value) ? false : alfanumericRegEx.test(value);
        }, 'NOT_ALFANUMERIC', true),
        month: createValidationMethod(function (value, param) {
            var month = parseInt(value, 10);

            return numberRegEx.test(value) && month >= 1 && month <= 12;
        }, 'NOT_MONTH', true),
        year: createValidationMethod(function (value, param) {
            var currentYear = new Date().getFullYear().toString().substring(2, 4);
            var maxYear = parseInt(currentYear) + 8; //Decided by Georg
            var year = parseInt(value, 10);

            return numberRegEx.test(value) && year >= currentYear && year <= maxYear;
        }, 'NOT_YEAR', true),
        validYear: createValidationMethod(function (value, param) {
            var currentYear = new Date().getFullYear();
            var year = parseInt(value, 10);

            return numberRegEx.test(value) && year <= currentYear && year >= 1900;
        }, 'NOT_VALID_YEAR'),
        validMonth: createValidationMethod(function (value, param) {
            var month = parseInt(value, 10);

            return numberRegEx.test(value) && month >= 1 && month <= 12;
        }, 'NOT_VALID_MONTH', true),
        // Check if a day number is within the range of days in month. Takes
        // a maxDaysInMonth number as argument. That number is assumed to be correct.
        validDay: createValidationMethod(function (value, param) {
            if (numberRegEx.test(value)) {
                value = parseFloat(value);
            }
            if (numberRegEx.test(param)) {
                param = parseFloat(param);
            }
            return value <= param && value > 0;
        }, 'NOT_VALID_DAY', true),
        sameAs: createValidationMethod(function (value, param) {
            return value === param;
        }, 'NOT_SAME_AS', true),
        notSameAs: createValidationMethod(function (value, param) {
            return value !== param;
        }, 'SAME_AS', true),
        couponCode: createValidationMethod(function (value, param) {
            return couponCodeRegEx.test(value);
        }, 'NOT_VALID_COUPON_CODE', true),
        noop: createValidationMethod(function (value, param) {
            return true;
        }, 'SHOULD_NOT_HAPPEN'),
        creditcard: createValidationMethod(function (value, param) {
            var luhnArr = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9],
                counter = 0,
                incNum,
                odd = false,
                temp = String(value).replace(/[^\d]/g, '');

            if (temp.length === 0 || !integerRegEx.test(value) || !supportedCardRegex.test(value)) {
                return false;
            }
            for (var i = temp.length - 1; i >= 0; --i) {
                incNum = parseInt(temp.charAt(i), 10);
                counter += (odd = !odd) ? incNum : luhnArr[incNum];
            }

            return (counter % 10 === 0);
        }, 'NOT_VALID_CREDITCARD_NUMBER'),
        notMaestro: createValidationMethod(function (value, params) {
            return !maestroRegex.test(value);
        }, 'MAESTRO_NOT_SUPPORTED'),
        phonePrefix: createValidationMethod(function (value, params) {
            return phonePrefixRegEx.test(value);
        }, 'NOT_VALID_PHONE_PREFIX'),
        strongPassword: createValidationMethod(function (value, params) {
            return (containCharacter.test(value) && containNumber.test(value)) ||
                (containSpecialCharacter.test(value) && containNumber.test(value)) ||
                (containSpecialCharacter.test(value) && containCharacter.test(value));
        }, 'NOT_STRONG_ENOUGH'),
        checkedRequired: createValidationMethod(function (value, params) {
            return value === params;
        }, 'CHECKED_REQUIRED'),
        //Server validation. There are two cases.
        // 1. If the param is a string it will be used as the name of the validation method to be
        //    used server side (f.ex. validate/command/[name]) and the value being validated will be the payload.
        // 2. If the param is an object the validation method will be the field 'name' and the payload will be the
        //    field 'payload' (f.ex. {name : 'validationMethodName', payload: payload} )
        server: function (value, param, command) {
            var name;
            var payload;

            param = ko.unwrap(param);
            if (_.isString(param)) {
                name = param;
                payload = value;
            } else {
                name = param.name;
                payload = param.payload;
            }
            if (param.ready === false) {
                return true;
            }

            return command.validate(name, payload);
        }
    };

    /**
     * A validator validates a value given a validation method and holds the current state of the validation.
     * @param validationMethodName name of validation method this validator will use
     * @param validationMethodParam parameter(s) for the validation method, f.ex the max in maxLength (maxLength: max)
     *        NOTE: If the param has an observable this will act as a dependency which means that if that observable changes
     *        we will force a validation.
     *
     */
    var Validator = function (validationMethodName, validationMethodParam, command) {
        var self = this;

        self.state = ko.observable(Validator.STATE_UNVALIDATED);
        self.error = ko.observable();

        var validationMethod = validationMethods[validationMethodName];

        if (_.isUndefined(validationMethod)) {

            ko.subscribable.fn.logger.waitForValue().then(function (LOG) {
                LOG.error('Validation method', validationMethodName, 'is not defined, will be ignored');
            });

            validationMethod = validationMethods.noop;
        }

        //Too keep track of long running validations being invalidated by newer validations.
        var currValidationCounter = 0;

        var resetScheduled = false;

        self.setResetMode = function (isReset) {
            if (isReset) {
                ++currValidationCounter; //Increase to ensure pending validations are ignored after this reset
                self.state(Validator.STATE_UNVALIDATED);
                self.error(undefined);
            }
            resetScheduled = isReset;
        };
        var params;
        var oldParamsDependencySubscription;

        self.validate = function (value) {
            if (resetScheduled) {
                return;
            }
            ++currValidationCounter;
            (function () {
                var validationForCounter = currValidationCounter;

                self.state(Validator.STATE_VALIDATING);
                self.error(undefined);

                if (_.isUndefined(params)) {
                    params = ko.pureComputed(function () {
                        return ko.toJS(validationMethodParam);
                    }).extend({
                        rateLimit: 0
                    });
                }

                //For each validation keep track of changes in params and re validate if they change. (f.ex sameAs should revalidate if the parameter to sameAs is changed)
                if (oldParamsDependencySubscription) {
                    oldParamsDependencySubscription.dispose();
                    oldParamsDependencySubscription = undefined;
                }
                oldParamsDependencySubscription = params.subscribe(function () {
                    self.validate(value);
                });

                //Just before the validation unwrap possible observables
                var deferredValidationResult = $.when(validationMethod(
                    ko.unwrap(value),
                    params(),
                    command()
                ));

                deferredValidationResult.done(function (validOrError) {
                    //If there was a newer validation after this, ignore the result
                    if (validationForCounter !== currValidationCounter) {
                        return;
                    }

                    if (validOrError === true) { //If validOrError is not true it will be the error-code
                        self.state(Validator.STATE_VALID);
                    } else {
                        self.state(Validator.STATE_NOT_VALID);
                        self.error(validOrError);
                    }
                });
                deferredValidationResult.fail(function () {
                    //If there was a newer validation after this, ignore the result
                    if (validationForCounter !== currValidationCounter) {
                        return;
                    }
                    self.state(Validator.STATE_BROKEN);
                    self.error('VALIDATION_BROKEN');
                });
            })();
        };
    };

    _.extend(Validator, {
        STATE_UNVALIDATED: 'STATE_UNVALIDATED',
        STATE_VALIDATING: 'STATE_VALIDATING',
        STATE_VALID: 'STATE_VALID',
        STATE_NOT_VALID: 'STATE_NOT_VALID',
        STATE_BROKEN: 'STATE_BROKEN'
    });

    function extendObservableWithValidations(target) {

        //Extend with field control features (set focus, select text, disable etc..)
        target.extend({
            field: {}
        });

        target._validations = {
            command: ko.observable(),
            validators: ko.observable({})
        };

        target.attachToCommand = function (commandToAttachTo) {
            target._validations.command(commandToAttachTo);
        };

        /**
         * The aggregated state will be determined by looking at the state of each validation.
         * Priority (the aggregated state will be resolved in the following order):
         * 1. All valid = STATE_VALID
         * 2. Any not valid = STATE_NOT_VALID
         * 3. Any broken = STATE_BROKEN
         * 4. Any unvalidated = STATE_UNVALIDATED
         * 5. Any validating = STATE_VALIDATING
         * else. STATE_BROKEN
         */
        target.validationState = ko.computed(function () {
            var states = _.map(target._validations.validators(), function (validator) {
                return validator.state();
            });

            //All valid
            if (states.length === 0 || _.every(states, function (state) {
                return state === Validator.STATE_VALID;
            })) {
                return Validator.STATE_VALID;
            }
            //Any not valid
            if (_.includes(states, Validator.STATE_NOT_VALID)) {
                return Validator.STATE_NOT_VALID;
            }
            //Any broken
            if (_.includes(states, Validator.STATE_BROKEN)) {
                return Validator.STATE_BROKEN;
            }
            //Any unvalidated
            if (_.includes(states, Validator.STATE_UNVALIDATED)) {
                return Validator.STATE_UNVALIDATED;
            }
            //Any validating
            if (_.includes(states, Validator.STATE_VALIDATING)) {
                return Validator.STATE_VALIDATING;
            }

            //If we end up here something is wrong.
            ko.subscribable.fn.logger.waitForValue().then(function (LOG) {
                LOG.error('Weird state in validations', states);
            });

            return Validator.STATE_BROKEN;
        });

        target.isValid = target.validationState.equals(Validator.STATE_VALID);

        target.isNotValid = target.validationState.equals(Validator.STATE_NOT_VALID);

        target.isUnvalidated = target.validationState.equals(Validator.STATE_UNVALIDATED);

        target.errors = ko.pureComputed(function () {
            return _.chain(target._validations.validators())
                .map(function (validator) {
                    return validator.error();
                })
                .filter(function (error) {
                    return !_.isUndefined(error);
                })
                               .flattenDeep().value();
        });

        target.validationState.subscribe(function (validationState) {
            target.disabled(validationState === Validator.STATE_VALIDATING);
        });

        //Define a function to do validation
        function validate(newValue) {
            _.each(target._validations.validators(), function (validator) {
                validator.validate(newValue);
            });
        }

        target.triggerValidation = function (soft) {
            if (soft && validationStateLoc() === Validator.STATE_UNVALIDATED) return;
            validate(target.peek());
        };

        target.validate = function () {
            return new Promise(function (resolve) {
                target.triggerValidation();
                target.validationState.subscribeAndExec(function (state) {
                    if (state !== Validator.STATE_VALIDATING) {
                        this.dispose();
                        resolve(target);
                    }
                });
            });
        };

        target.setResetMode = function (isReset) {
            _.each(target._validations.validators(), function (validator) {
                validator.setResetMode(isReset);
            });
        };
    }

    ko.extenders.validations = function (target, validations) {

        if (!target._validations) {
            extendObservableWithValidations(target);
        }

        target._validations.validators(_.extend(
            target._validations.validators(),
            _.mapValues(validations || {}, function (param, validationMethodName) {
                return new Validator(validationMethodName, param, target._validations.command);
            })
        ));

        return target;
    };

});

